// The only way screens call the broker: same origin, bearer token attached, errors in the
// broker's `{ error: { code, message } }` shape.

import { accessToken } from './auth';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId: string | null,
    /** Field name → reason, for input the broker refused. */
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function toApiError(res: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = `HTTP ${res.status}`;
  let fields: Record<string, string> = {};
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string; fields?: Record<string, string> };
    };
    code = body.error?.code ?? code;
    message = body.error?.message ?? message;
    fields = body.error?.fields ?? {};
  } catch {
    // Non-JSON error page (e.g. a gateway); keep the generic message.
  }
  return new ApiError(res.status, code, message, res.headers.get('x-correlation-id'), fields);
}

async function get<T>(path: `/api/${string}`): Promise<T> {
  const res = await fetch(path, {
    headers: { accept: 'application/json', authorization: `Bearer ${await accessToken()}` },
  });
  if (res.ok) return (await res.json()) as T;
  throw await toApiError(res);
}

// Reads started early (prefetch), each handed to the first apiGet of the same path. Unused
// ones expire, so a later visit to the page always reads afresh.
const PREFETCH_MS = 15_000;
const early = new Map<string, { result: Promise<unknown>; at: number }>();

/** Starts GETs now; the page's own apiGet of the same path takes the answer (P5.1b). */
export function prefetch(paths: readonly `/api/${string}`[]): void {
  for (const path of paths) {
    const result = get(path);
    // Handled here too, so an unused failed read is not an unhandled rejection.
    result.catch(() => undefined);
    early.set(path, { result, at: Date.now() });
  }
}

export async function apiGet<T>(path: `/api/${string}`): Promise<T> {
  const pre = early.get(path);
  if (pre) {
    early.delete(path);
    if (Date.now() - pre.at < PREFETCH_MS) return pre.result as Promise<T>;
  }
  return get<T>(path);
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH',
  path: `/api/${string}`,
  body: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      authorization: `Bearer ${await accessToken()}`,
    },
    body: JSON.stringify(body),
  });
  if (res.ok) return (await res.json()) as T;
  throw await toApiError(res);
}

/** A POST that answers with a file (the RFQ PDF, D88): the bytes and their file name. */
export async function apiSendForBytes(
  path: `/api/${string}`,
  body: unknown,
): Promise<{ bytes: Uint8Array; type: string; name: string }> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${await accessToken()}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res);
  const type = (res.headers.get('content-type') ?? '').split(';')[0]?.trim() ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/.exec(
    res.headers.get('content-disposition') ?? '',
  )?.[1];
  return {
    bytes: new Uint8Array(await res.arrayBuffer()),
    type,
    name: encoded ? decodeURIComponent(encoded) : 'document.pdf',
  };
}

/** One file as the raw request body; the broker checks name, size and content itself. */
export async function apiUpload<T>(
  path: `/api/${string}`,
  file: File,
  extra: Record<`x-${string}`, string> = {},
): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      ...extra,
      accept: 'application/json',
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      authorization: `Bearer ${await accessToken()}`,
    },
    body: file,
  });
  if (res.ok) return (await res.json()) as T;
  throw await toApiError(res);
}

/** A file from the broker as bytes (for the Finance pack, merged in the browser). */
export async function apiBytes(
  path: `/api/${string}`,
): Promise<{ bytes: Uint8Array; type: string }> {
  const res = await fetch(path, { headers: { authorization: `Bearer ${await accessToken()}` } });
  if (!res.ok) throw await toApiError(res);
  const type = (res.headers.get('content-type') ?? '').split(';')[0]?.trim() ?? '';
  return { bytes: new Uint8Array(await res.arrayBuffer()), type };
}

/** Bytes made in the browser, saved through a temporary link. */
export function saveBytes(bytes: Uint8Array, name: string, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** A file from the broker, saved through a temporary link; nothing is kept afterwards. */
export async function apiDownload(path: `/api/${string}`, fallbackName: string): Promise<void> {
  const res = await fetch(path, { headers: { authorization: `Bearer ${await accessToken()}` } });
  if (!res.ok) throw await toApiError(res);
  const disposition = res.headers.get('content-disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1];
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = encoded ? decodeURIComponent(encoded) : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
